import type { ElementNode } from "../types";
import type { Element } from "../ui/types";
import { toNarrowBuilderNode } from "./narrowBuilderNodeSerializer";

/**
 * Live editor trees (mega menus, lightboxes) persist raw `Element` shapes with
 * flat `styles` and structural fields (`columns`, `column`, `background`, …).
 * Public render expects serialized `ElementNode` from `toBuilderNode`.
 */
export function isLiveEditorElement(
	node: Element | ElementNode
): node is Element {
	if (!node || typeof node !== "object") {
		return false;
	}
	const record = node as Record<string, unknown>;
	if (record.styles == null) {
		return false;
	}
	// Serialized nodes use tokenized `style`; live canvas nodes use flat `styles`.
	return record.style == null;
}

/**
 * Convert a live editor element tree for `renderBuilderNode`. Idempotent for
 * serialized nodes. Uses the narrow, 17-type converter (Phase 3 of the Lighthouse
 * plan) instead of the admin adapter's full `toBuilderNode` — the only two callers
 * of this function are mega-menu and lightbox rendering, and both canvases are
 * restricted to that same 17-type palette, so the full ~114KB admin editor file
 * no longer needs to be part of the public runtime's bundle for this fallback.
 */
export function normalizeLiveElementForRuntime(
	node: Element | ElementNode
): ElementNode {
	if (!isLiveEditorElement(node)) {
		return node as ElementNode;
	}
	return toNarrowBuilderNode(node);
}
